import {memo, useContext, useRef, useState} from 'react'

import cx from 'classnames'
import PropTypes from 'prop-types'

import AtomHelpText from '@s-ui/react-atom-help-text'
import AtomLabel from '@s-ui/react-atom-label'
import AtomTag, {atomTagDesigns} from '@s-ui/react-atom-tag'
import Context from '@s-ui/react-context'
import MoleculeTabs, {MoleculeTab, moleculeTabsTypes, moleculeTabsVariants} from '@s-ui/react-molecule-tabs'
import {filterBasicTypes} from '@adv-mt/mt-filter-basic'
import {FilterBasicLegacySingleSelection} from '@adv-mt/mt-filter-basic-legacy'
import FilterWrapperBasic, {filterWrapperBasicAllSelectionsId as allSelectionsId} from '@adv-mt/mt-filter-wrapper-basic'
import FormSelect from '@adv-mt/mt-form-select'
import {useAnalyticsTrack} from '@adv-mt/mt-tracking-use-analytics'

import {FIELD_NAME, LABEL_FIRST_OPTION, LEGACY_WRAPPER} from './config.js'
import useChips from './useChips.js'
import useOptions from './useOptions.js'
import {getPlaceholder} from './utils.js'

const FormSelectRange = ({
  fromChipSeeds,
  toChipSeeds,
  toChipSeedsBase,
  defaultActiveTabIndex = 1,
  description,
  fieldName: filterName,
  formatterLabel = ({value}) => value,
  helpText,
  initialSearch,
  isBasicContent = false,
  isInline,
  label,
  labelAccessibility,
  onChange = () => {},
  options,
  overlayContentRef,
  pageName = '',
  suffix,
  suffixIcon,
  type,
  value = {
    from: undefined,
    to: undefined
  },
  withLabel = true,
  wrapper = LEGACY_WRAPPER
}) => {
  const {domain, i18n} = useContext(Context)
  const {options: currentOptions} = useOptions({from: options.from, to: options.to, value})

  const {fromChips, toChips} = useChips({
    seeds: fromChipSeeds || [],
    toSeeds: toChipSeeds,
    toSeedsBase: toChipSeedsBase,
    fromValue: value.from,
    toValue: value.to
  })

  const [focusedField, setFocusedField] = useState(null)
  const blurTimeoutRef = useRef(null)

  const handleInputFocus = name => {
    clearTimeout(blurTimeoutRef.current)
    setFocusedField(name)
  }

  const handleInputBlur = () => {
    blurTimeoutRef.current = setTimeout(() => setFocusedField(null), 150)
  }

  const {track} = useAnalyticsTrack()
  const config = domain.get('config')
  const TRACKING_FILTER_NAMES = config.get('TRACKING_FILTER_NAMES')
  const {TRACKING_EVENT_NAMES} = config.get('tracking')
  const {DEFAULT_FILTER_VALUE} = config.get('search')
  const {DEFAULT_FILTERS_SEARCH} = config.get('searchFilters')

  const hasHelpText = Boolean(helpText)

  const formSelectRangeClassName = cx('mt-FormSelectRange', {
    'mt-FormSelectRange--inline': isInline
  })

  const getDerivedOptionsFromSelected = ({namespace, selectedOptionValue, isFieldFrom, isFieldTo}) => {
    const {from: fromValue, to: toValue} = value
    const isDefaultOption = selectedOptionValue === 0

    if (isFieldFrom && !isDefaultOption && selectedOptionValue > toValue) {
      return {from: selectedOptionValue, to: DEFAULT_FILTER_VALUE}
    }

    if (isFieldTo && !isDefaultOption && selectedOptionValue < fromValue) {
      return {from: DEFAULT_FILTER_VALUE, to: selectedOptionValue}
    }

    return {
      ...value,
      [namespace]: selectedOptionValue
    }
  }

  const _handleOnChange =
    ({name}) =>
    (ev, {value: selectedOptionValue}) => {
      const isFieldFrom = name === FIELD_NAME.FROM
      const isFieldTo = name === FIELD_NAME.TO
      const lastFilter = TRACKING_FILTER_NAMES[filterName][name.toUpperCase()]

      onChange(ev, {
        field: filterName,
        value: getDerivedOptionsFromSelected({
          namespace: name,
          selectedOptionValue,
          isFieldFrom,
          isFieldTo
        }),
        lastFilter
      })
    }

  const RANGE_STEP = 1000

  const handleChange = ({name, value: selectedValue}) => {
    const [currentValue] = selectedValue
    const newValue = currentValue === allSelectionsId ? DEFAULT_FILTERS_SEARCH[filterName][name] : Number(currentValue)

    const next = {
      from: value.from || null,
      to: value.to || null,
      [name]: newValue
    }

    if (isInputNumber) {
      if (name === FIELD_NAME.FROM && next.from != null && next.to != null && next.from >= next.to) {
        next.to = next.from + RANGE_STEP
      } else if (name === FIELD_NAME.TO && next.to != null && next.from != null && next.to <= next.from) {
        next.from = next.to - RANGE_STEP
      }
    }

    onChange(null, {
      field: filterName,
      value: next,
      lastFilter: TRACKING_FILTER_NAMES[filterName][name.toUpperCase()]
    })
  }

  const handleReset = ({filters}) => {
    // eslint-disable-next-line no-nested-ternary
    const filterKey = filters.length === 1 ? filters[0] : value.from === null ? filters[1] : filters[0]

    onChange(null, {
      field: filterName,
      value: {
        ...value,
        [filterKey]: DEFAULT_FILTERS_SEARCH[filterName][filterKey]
      }
    })
  }

  const handleSelectPrice =
    ({name}) =>
    ({id}) =>
      _handleOnChange({name})(null, {value: id})

  const getCommonProps = namespace => {
    const {name, options: optionsList} = options[namespace]
    const placeholder = i18n.t(LABEL_FIRST_OPTION[namespace])
    const defaultOption = {id: 0, label: placeholder}

    return {
      isFitted: hasHelpText,
      name,
      onChange: isBasicContent ? handleSelectPrice({name}) : _handleOnChange({name}),
      options: [defaultOption, ...optionsList],
      overlayContentRef,
      pageName,
      placeholder,
      value: value[namespace]
    }
  }

  if (wrapper === LEGACY_WRAPPER) {
    return isBasicContent ? (
      <div className="mt-FormSelectRangeBasicContent">
        <MoleculeTabs
          defaultActiveTabIndex={defaultActiveTabIndex}
          type={moleculeTabsTypes.FULLWIDTH}
          variant={moleculeTabsVariants.HIGHLIGHTED}
        >
          <MoleculeTab numTab={1} label={i18n.t('COMMON_FROM')}>
            <div className="mt-FormSelectRangeBasicContent-tab">
              <FilterBasicLegacySingleSelection {...getCommonProps(FIELD_NAME.FROM)} />
            </div>
          </MoleculeTab>
          <MoleculeTab numTab={2} label={i18n.t('COMMON_TO')}>
            <div className="mt-FormSelectRangeBasicContent-tab">
              <FilterBasicLegacySingleSelection {...getCommonProps(FIELD_NAME.TO)} />
            </div>
          </MoleculeTab>
        </MoleculeTabs>
      </div>
    ) : (
      <div className={formSelectRangeClassName}>
        <div className="mt-FormSelectRange-labelWrapper">
          <AtomLabel text={label} />
        </div>
        <div className="mt-FormSelectRangeWrapper">
          <FormSelect
            {...getCommonProps(FIELD_NAME.FROM)}
            field={filterName}
            fieldType={FIELD_NAME.FROM}
            initialSearch={initialSearch}
          />
          <FormSelect
            {...getCommonProps(FIELD_NAME.TO)}
            field={filterName}
            fieldType={FIELD_NAME.TO}
            initialSearch={initialSearch}
          />
        </div>
        {hasHelpText && <AtomHelpText text={helpText} />}
      </div>
    )
  }

  const fromPlaceholder = getPlaceholder({
    basePlaceholder: i18n.t(LABEL_FIRST_OPTION[FIELD_NAME.FROM]),
    formatter: formatterLabel,
    value: value.from
  })

  const toPlaceholder = getPlaceholder({
    basePlaceholder: i18n.t(LABEL_FIRST_OPTION[FIELD_NAME.TO]),
    formatter: formatterLabel,
    value: value.to
  })

  const mainPlaceholder = (value.from && fromPlaceholder) || (value.to && toPlaceholder) || label

  const isInputNumber = type === filterBasicTypes.INPUT_NUMBER

  const activeChips = focusedField === FIELD_NAME.TO ? toChips : focusedField === FIELD_NAME.FROM ? fromChips : []

  const handleChipClick = chip => {
    handleChange({name: focusedField, value: [String(chip)]})
    const chipEventName =
      filterName === 'price' ? TRACKING_EVENT_NAMES.FILTER_PRICE_CHIP_USED : TRACKING_EVENT_NAMES.FILTER_KM_CHIP_USED
    track({event: chipEventName, data: {hit_information: String(chip)}})
  }

  return (
    <>
      <FilterWrapperBasic
        description={description}
        filters={[
          {
            allSelectionsLabel: i18n.t(LABEL_FIRST_OPTION[FIELD_NAME.FROM]),
            chips: isInputNumber ? fromChips : undefined,
            label: isInputNumber
              ? i18n.t(LABEL_FIRST_OPTION[FIELD_NAME.FROM])
              : `${labelAccessibility ?? label} ${i18n.t(LABEL_FIRST_OPTION[FIELD_NAME.FROM])}`,
            name: FIELD_NAME.FROM,
            options: currentOptions.from,
            placeholder: fromPlaceholder || i18n.t(LABEL_FIRST_OPTION[FIELD_NAME.FROM]),
            suffix: isInputNumber ? suffix : undefined,
            suffixIcon,
            type,
            showLabel: isInputNumber
          },
          {
            allSelectionsLabel: i18n.t(LABEL_FIRST_OPTION[FIELD_NAME.TO]),
            chips: isInputNumber ? toChips : undefined,
            label: isInputNumber
              ? i18n.t(LABEL_FIRST_OPTION[FIELD_NAME.TO])
              : `${labelAccessibility ?? label} ${i18n.t(LABEL_FIRST_OPTION[FIELD_NAME.TO])}`,
            name: FIELD_NAME.TO,
            options: currentOptions.to,
            placeholder: toPlaceholder || i18n.t(LABEL_FIRST_OPTION[FIELD_NAME.TO]),
            suffix: isInputNumber ? suffix : undefined,
            suffixIcon,
            type,
            showLabel: isInputNumber
          }
        ]}
        group={`${filterName}Group`}
        helpText={helpText}
        legend={label}
        placeholder={mainPlaceholder}
        overlayContentRef={overlayContentRef}
        onBlur={isInputNumber ? handleInputBlur : undefined}
        onChange={handleChange}
        onFocus={isInputNumber ? handleInputFocus : undefined}
        onReset={handleReset}
        showLegend={withLabel}
        value={{
          [FIELD_NAME.FROM]: [value.from || allSelectionsId],
          [FIELD_NAME.TO]: [value.to || allSelectionsId]
        }}
        wrapper={wrapper}
      />
      {isInputNumber && activeChips.length > 0 && (
        <div className="mt-FormSelectRange-mobileChips">
          {activeChips.map(chip => (
            <AtomTag
              className="mt-FormSelectRange-mobileChipsItem"
              design={atomTagDesigns.OUTLINE}
              key={chip}
              label={`${chip.toLocaleString(i18n.culture, {useGrouping: true})}${suffix ? ` ${suffix}` : ''}`}
              onClick={() => handleChipClick(chip)}
              onMouseDown={event => event.preventDefault()}
            />
          ))}
        </div>
      )}
    </>
  )
}

FormSelectRange.displayName = 'FormSelectRange'

FormSelectRange.propTypes = {
  fromChipSeeds: PropTypes.arrayOf(PropTypes.number),
  toChipSeeds: PropTypes.arrayOf(PropTypes.number),
  toChipSeedsBase: PropTypes.arrayOf(PropTypes.number),
  defaultActiveTabIndex: PropTypes.number,
  description: PropTypes.string,
  fieldName: PropTypes.string.isRequired,
  formatterLabel: PropTypes.func,
  helpText: PropTypes.string,
  isBasicContent: PropTypes.bool,
  initialSearch: PropTypes.object,
  isInline: PropTypes.bool,
  label: PropTypes.string.isRequired,
  labelAccessibility: PropTypes.string,
  onChange: PropTypes.func,
  options: PropTypes.shape({
    from: PropTypes.shape({
      options: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.number,
          label: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        })
      ).isRequired,
      name: PropTypes.string.isRequired,
      placeholder: PropTypes.string
    }),
    to: PropTypes.shape({
      options: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.number,
          label: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        })
      ).isRequired,
      name: PropTypes.string.isRequired,
      placeholder: PropTypes.string
    })
  }),
  overlayContentRef: PropTypes.node,
  pageName: PropTypes.string,
  suffix: PropTypes.string,
  suffixIcon: PropTypes.node,
  type: PropTypes.string,
  value: PropTypes.object,
  withLabel: PropTypes.bool,
  wrapper: PropTypes.string
}

export {FIELD_NAME}
export default memo(FormSelectRange)
